import { useState, useEffect } from 'react';
import { getUserProgress, getRoomGameData, recordQuizAnswer } from '../utils/api';

/**
 * Custom hook to manage game state
 */
const useGameState = () => {
  // Basic game state
  const [currentStep, setCurrentStep] = useState(0);
  const [messages, setMessages] = useState([]);
  const [showMessage, setShowMessage] = useState(true);
  const [showQuestions, setShowQuestions] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [showAnswerModal, setShowAnswerModal] = useState(false);
  const [answerInput, setAnswerInput] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showFailureAlert, setShowFailureAlert] = useState(false);
  const [puzzlesCompleted, setPuzzlesCompleted] = useState(false);

  // Game data from backend
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [roomNumber, setRoomNumber] = useState(null);

  // Load user data from localStorage on mount
  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedRoomNumber = localStorage.getItem('roomNumber');
    
    if (storedUserId) {
      setUserId(storedUserId);
    }
    
    if (storedRoomNumber) {
      setRoomNumber(storedRoomNumber);
    }
  }, []);

  /**
   * Initialize messages for a specific step
   */
  const initializeMessagesForStep = (step) => {
    console.log(`Initializing messages for step ${step}`);
    // In a real implementation, this would load messages for the specific step
  };

  /**
   * Handle moving to the next step in the game
   */
  const handleNext = async () => {
    // Set showQuestions to true
    setShowQuestions(true);
    setSelectedAnswer(null);
    setCurrentStep(prev => prev + 1);
    console.log("🔄 useGameState: Moving to next step, showing questions:", { showQuestions: true, currentStep: currentStep + 1 });
  };

  /**
   * Handle answer selection for multiple choice questions
   */
  const handleAnswerSelect = async (answer, text) => {
    setSelectedAnswer(text);
    setShowQuestions(false);
    console.log("🔄 useGameState: Answer selected, hiding questions");
    
    try {
      // Add user message
      const newUserMessage = {
        text,
        isUser: true,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, newUserMessage]);
      console.log("🔄 useGameState: Added user message", newUserMessage);
      
      // Add feedback message
      const feedbackMessage = {
        text: "Thank you for your answer!",
        isUser: false,
        isFeedback: true,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, feedbackMessage]);
      console.log("🔄 useGameState: Added feedback message", feedbackMessage);
      
      // Show success alert
      setShowSuccessAlert(true);
      
      // Move to next step
      setTimeout(() => {
        setCurrentStep(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error handling answer selection:', error);
      
      // Show error message
      const errorMessage = {
        text: `Error: ${error.message || 'Failed to process your answer'}`,
        isUser: false,
        isError: true,
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      console.log("🔄 useGameState: Added error message", errorMessage);
    }
  };

  /**
   * Handle showing a hint
   */
  const handleHint = () => {
    setShowHint(true);
  };

  /**
   * Show the answer input modal
   */
  const handleAnswerInput = () => {
    setShowAnswerModal(true);
  };

  /**
   * Handle answer submission from modal
   */
  const handleAnswerSubmit = async () => {
    if (!answerInput.trim()) return;
    
    setShowAnswerModal(false);
    
    // Add user answer as a message
    setMessages(prev => [...prev, {
      text: answerInput,
      isUser: true,
      timestamp: new Date()
    }]);
    
    // Clear input
    setAnswerInput("");
    
    // Show success alert (in a real app, would check if answer is correct)
    setShowSuccessAlert(true);
  };

  /**
   * Handle closing the success alert
   */
  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
    setCurrentStep(prev => prev + 1);
  };

  /**
   * Handle closing the failure alert
   */
  const handleCloseFailureAlert = () => {
    setShowFailureAlert(false);
  };

  /**
   * Handle going back from hint view
   */
  const handleBackFromHint = () => {
    setShowHint(false);
  };

  return {
    // State
    currentStep,
    messages,
    showMessage,
    showQuestions,
    selectedAnswer,
    showHint,
    showAnswerModal,
    answerInput,
    showSuccessAlert,
    showFailureAlert,
    puzzlesCompleted,
    loading,
    error,
    gameData,
    roomNumber,
    
    // Actions
    setAnswerInput,
    setShowAnswerModal,
    setShowQuestions,
    setMessages,
    handleNext,
    handleAnswerSelect,
    handleHint,
    handleBackFromHint,
    handleAnswerInput,
    handleAnswerSubmit,
    handleCloseSuccessAlert,
    handleCloseFailureAlert,
    setPuzzlesCompleted,
    setCurrentStep,
  };
};

export default useGameState; 